import React from "react";
import Badge from "react-bootstrap/Badge";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

/**
 * ThreadList: conversations for one practice, unread first.
 *
 * ThreadRow is shared by the dashboard card (compact, capped) and the inbox
 * modal (full, filterable) so the two can never drift in how a thread reads.
 *
 * The search box and filter are NOT here: the inbox pane owns one header that
 * stays put while the list below it swaps between threads and the contact
 * picker (see InboxModal). A header that moved or duplicated itself when the
 * pane changed mode would read as a different screen.
 */

/** en-GB per §1.14: time today, "Yesterday", then a short date. */
export function relativeTime(value) {
  if (!value) return "";
  const date = new Date(value);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
}

export function ThreadRow({ conversation, active, onSelect }) {
  const unread = conversation.unreadCount > 0;
  const name =
    conversation.patientName ||
    conversation.accountName ||
    conversation.profileName ||
    conversation.displayPhone;

  return (
    <button
      type="button"
      className={[
        "wa-thread",
        unread ? "wa-thread-unread" : "",
        active ? "wa-thread-active" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={() => onSelect(conversation)}
    >
      <div className="d-flex align-items-center gap-2">
        <OverlayTrigger
          placement="top"
          overlay={
            <Tooltip>
              {conversation.windowOpen
                ? "You can reply freely. The 24-hour window is open."
                : "The 24-hour window has closed, so a template is required."}
            </Tooltip>
          }
        >
          <i
            className={`fa-solid fa-circle ${
              conversation.windowOpen ? "text-success" : "text-muted"
            }`}
            style={{ fontSize: "0.5rem" }}
          />
        </OverlayTrigger>

        <span className={`flex-fill text-truncate ${unread ? "fw-semibold" : ""}`}>
          {name}
        </span>

        {/* A thread is identified by whichever subject it was opened for.
            An account holder is not an unlinked patient. The two are separate
            entities and are never matched to each other by phone number. */}
        {!conversation.patientId && !conversation.accountId && (
          <Badge bg="secondary">Unknown number</Badge>
        )}
        {unread && <Badge bg="danger">{conversation.unreadCount}</Badge>}
        <small className="text-muted">
          {relativeTime(conversation.lastMessageAt)}
        </small>
      </div>

      <small className="wa-thread-preview text-muted">
        {conversation.lastMessagePreview || "No messages yet"}
      </small>
    </button>
  );
}

export default function ThreadList({ conversations, activeId, onSelect, loading }) {
  return (
    <>
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading conversations…</p>
        </div>
      ) : !conversations.length ? (
        <p className="text-center text-muted py-4 mb-0">
          No conversations found.
        </p>
      ) : (
        conversations.map((c) => (
          <ThreadRow
            key={c._id}
            conversation={c}
            active={String(c._id) === String(activeId)}
            onSelect={onSelect}
          />
        ))
      )}
    </>
  );
}
